import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { ThreeBand } from "../components/macaron/ThreeBand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAL_URL =
  "https://calendar.google.com/calendar/u/0/appointments/schedules/AcZssZ3EhspfrbOw3sg0Leq6ckkYJMWCNj_BgVIg47LfAWaGU8jIKSSUqRmuBifnw-yQrkw5JPhANVVD";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (d) => ({ opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], delay: d } }),
};

const PREFS = [
  { key: "call", label: "Call" },
  { key: "text", label: "Text" },
  { key: "email", label: "Email" },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", brand: "", preferred_contact: "email", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.message) {
      setError("Please fill in your name, email, and message.");
      return;
    }
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("done");
    } catch (err) {
      setStatus("error");
      setError("Something went wrong. Please try again.");
    }
  };

  return (
    <section className="gtku-section" data-testid="contact-page">
      <div className="mac-container" style={{ position: "relative", zIndex: 1, width: "100%", textAlign: "center" }}>
        <div className="cta-glow" />
        <div style={{ position: "relative", zIndex: 1, maxWidth: 600, margin: "0 auto" }}>
          <motion.div variants={fadeUp} custom={0} initial="hidden" animate="show">
            <div className="mac-eyebrow-row center">
              <ThreeBand />
              <p className="mac-eyebrow">CONTACT US</p>
            </div>
          </motion.div>

          <motion.h1
            variants={fadeUp}
            custom={0.1}
            initial="hidden"
            animate="show"
            className="mac-display"
            data-testid="contact-headline"
            style={{ fontSize: "clamp(40px, 6.5vw, 68px)", marginTop: 24 }}
          >
            Let's build<br /><span className="mac-gradient-text">something brave.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            custom={0.25}
            initial="hidden"
            animate="show"
            className="mac-body"
            style={{ margin: "24px auto 0", maxWidth: 520, fontSize: 16 }}
          >
            Tell us about your toughest display launch, compliance gap, or brand challenge. Our team will be in touch.
          </motion.p>

          <motion.div variants={fadeUp} custom={0.35} initial="hidden" animate="show" style={{ marginTop: 26 }}>
            <a
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mac-btn mac-btn-lg"
              data-testid="contact-calendar-btn"
              style={{ textDecoration: "none" }}
            >
              Book a Call on My Calendar <ArrowRight size={16} strokeWidth={2.5} />
            </a>
            <p style={{ marginTop: 14, fontFamily: "Inter, sans-serif", fontSize: 12, letterSpacing: "0.16em", textTransform: "uppercase", color: "rgba(255,255,255,0.45)" }}>
              Or send us a message
            </p>
          </motion.div>

          <motion.div variants={fadeUp} custom={0.4} initial="hidden" animate="show">
            <div className="mac-card" style={{ padding: 32, position: "relative", marginTop: 40, textAlign: "left" }}>
              <div className="mac-wide-bar" />
              {status === "done" ? (
                <div data-testid="contact-success" style={{ textAlign: "center", padding: "24px 0" }}>
                  <div
                    style={{
                      width: 56, height: 56, borderRadius: "50%", background: "var(--holo)",
                      display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 18px",
                    }}
                  >
                    <Check size={26} color="#1F2540" strokeWidth={3} />
                  </div>
                  <h3 className="mac-card-title">Thank you.</h3>
                  <p className="mac-body" style={{ marginTop: 12, fontSize: 14 }}>
                    We've received your message and will be in touch shortly.
                  </p>
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-col gap-4">
                  <div>
                    <label className="mac-label" htmlFor="c-name">Name</label>
                    <input id="c-name" className="mac-input" data-testid="contact-name" value={form.name} onChange={update("name")} placeholder="Your name" />
                  </div>
                  <div>
                    <label className="mac-label" htmlFor="c-email">Email</label>
                    <input id="c-email" type="email" className="mac-input" data-testid="contact-email" value={form.email} onChange={update("email")} placeholder="you@brand.com" />
                  </div>
                  <div>
                    <label className="mac-label" htmlFor="c-phone">Phone <span style={{ opacity: 0.5, letterSpacing: 0 }}>(optional)</span></label>
                    <input id="c-phone" type="tel" className="mac-input" data-testid="contact-phone" value={form.phone} onChange={update("phone")} placeholder="+1 (___) ___-____" />
                  </div>
                  <div>
                    <label className="mac-label" htmlFor="c-brand">Brand <span style={{ opacity: 0.5, letterSpacing: 0 }}>(optional)</span></label>
                    <input id="c-brand" className="mac-input" data-testid="contact-brand" value={form.brand} onChange={update("brand")} placeholder="Brand name" />
                  </div>
                  <div>
                    <label className="mac-label">Preferred contact method</label>
                    <div style={{ display: "flex", gap: 8 }}>
                      {PREFS.map((p) => {
                        const active = form.preferred_contact === p.key;
                        return (
                          <button
                            type="button"
                            key={p.key}
                            data-testid={`contact-pref-${p.key}`}
                            onClick={() => setForm({ ...form, preferred_contact: p.key })}
                            style={{
                              flex: 1,
                              padding: "10px 0",
                              borderRadius: 100,
                              border: active ? "1px solid var(--pink)" : "1px solid rgba(245,235,224,0.16)",
                              background: active ? "var(--pink)" : "transparent",
                              color: active ? "var(--bg)" : "var(--white)",
                              fontFamily: "Inter, sans-serif",
                              fontWeight: 600,
                              fontSize: 12,
                              letterSpacing: "0.12em",
                              textTransform: "uppercase",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                            }}
                          >
                            {p.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <div>
                    <label className="mac-label" htmlFor="c-message">Message</label>
                    <textarea id="c-message" className="mac-textarea" data-testid="contact-message" rows={4} value={form.message} onChange={update("message")} placeholder="Tell us what you're working on…" />
                  </div>
                  {error && <p data-testid="contact-error" style={{ color: "var(--pink)", fontSize: 13, margin: 0 }}>{error}</p>}
                  <button type="submit" className="mac-btn" data-testid="contact-submit-btn" disabled={status === "sending"} style={{ justifyContent: "center", marginTop: 4 }}>
                    {status === "sending" ? "Sending…" : <>Send message <ArrowRight size={15} strokeWidth={2.5} /></>}
                  </button>
                </form>
              )}
            </div>

            <p style={{ marginTop: 22, fontFamily: "Inter, sans-serif", fontWeight: 500, fontSize: 12, color: "rgba(255,255,255,0.55)", letterSpacing: "0.04em" }}>
              themacaron.ai · hello@themacaron.ai · NYC · SF
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
